// @ts-nocheck
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocalization, type Language } from '../../../config/localization';
import Icon, { ICONS } from '../../ui/icons';
import GalleryImageCard from './GalleryImageCard';
import type { GalleryImage } from './GalleryContent';

interface Collection {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_private: boolean;
  can_edit: boolean;
  total_images?: number;
}

interface VirtualizedImageGridProps {
  images: GalleryImage[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  viewMode: 'grid' | 'list';
  zoomLevel: 1 | 2 | 3 | 4 | 5;
  language: Language;
  organizationId: number;
  selectionMode?: boolean;
  selectedIds?: Set<number>;
  onToggleSelection?: (id: number, index: number, shiftKey: boolean) => void;
  onDragSelectStart?: (id: number, index: number) => void;
  onDragSelectEnter?: (id: number) => void;
  isDragSelecting?: boolean;
  onMoveToTrash?: (id: number) => void;
  onMark?: (id: number, mark: 'like' | 'dislike' | 'approved' | null) => void;
  onRestore?: (id: number) => void;
  isTrashMode?: boolean;
  // Collection actions
  collections?: Collection[];
  onAddToCollections?: (imageId: number, collectionIds: number[]) => Promise<void>;
  onRemoveFromCollection?: (imageId: number, collectionId: number) => Promise<void>;
  // Toast notification
  onShowToast?: (message: string, type: 'success' | 'error') => void;
  // Lightbox callback — delegates preview to parent
  onImageClick?: (index: number) => void;
  // Optional scroll container ref for infinite scroll (defaults to window)
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const VirtualizedImageGrid: React.FC<VirtualizedImageGridProps> = ({
  images,
  loading,
  hasMore,
  onLoadMore,
  viewMode,
  zoomLevel,
  language,
  organizationId,
  selectionMode = false,
  selectedIds = new Set(),
  onToggleSelection,
  onDragSelectStart,
  onDragSelectEnter,
  isDragSelecting = false,
  onMoveToTrash,
  onMark,
  onRestore,
  isTrashMode = false,
  collections = [],
  onAddToCollections,
  onRemoveFromCollection,
  onShowToast,
  onImageClick,
  scrollContainerRef: externalScrollRef
}) => {
  const t = useLocalization(language);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const cooldownRef = useRef(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Use refs for loading and onLoadMore to avoid re-creating the observer
  // (and clearing the cooldown timer) every time these change. Without this,
  // the useEffect cleanup clears the cooldown reset timer, leaving
  // cooldownRef stuck at true and breaking infinite scroll after first load.
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // IntersectionObserver-based infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // When hasMore is false, don't observe — no more items to load
    if (!hasMore) return;

    const root = externalScrollRef?.current || null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingRef.current && !cooldownRef.current) {
          onLoadMoreRef.current();
          // Start cooldown to prevent rapid-fire requests
          cooldownRef.current = true;
          cooldownTimerRef.current = setTimeout(() => {
            cooldownRef.current = false;
          }, 1000);
        }
      },
      { root, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownRef.current = false;
    };
  }, [hasMore, externalScrollRef]);

  // Generate grid classes based on zoom level (matching BigGalleryWidget)
  const getGridClasses = () => {
    const zoomClasses = {
      1: 'grid-cols-4 sm:grid-cols-5 lg:grid-cols-7 xl:grid-cols-8', // Small (many columns)
      2: 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7', // Medium-
      3: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6', // Medium (default)
      4: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5', // Medium+
      5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'  // Large (few columns)
    };
    return zoomClasses[zoomLevel] || zoomClasses[3];
  };

  if (images.length === 0 && !loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <Icon name={ICONS.GALLERY} size={96} className="mx-auto mb-6 text-gray-500" />
            <h3 className="text-2xl font-bold mb-4 text-gray-400">
              {t.galleryPage?.noResults || 'No images found'}
            </h3>
            <p className="text-gray-500 mb-6">
              {t.galleryPage?.noResultsDesc || 'Try adjusting your search or filter settings'}
            </p>
          </div>
        </div>
        {/* Sentinel must always be in the DOM for IntersectionObserver setup */}
        <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">

      {/* Grid */}
      <div className="flex-1">
        {viewMode === 'grid' ? (
          <div className={`grid ${getGridClasses()} gap-3 p-3`}>
            {images.map((image, index) => (
              <GalleryImageCard
                key={image.id}
                image={image}
                index={index}
                viewMode={viewMode}
                language={language}
                organizationId={organizationId}
                selectionMode={selectionMode}
                isSelected={selectedIds.has(image.id)}
                onToggleSelection={onToggleSelection}
                onDragSelectStart={onDragSelectStart}
                onDragSelectEnter={onDragSelectEnter}
                isDragSelecting={isDragSelecting}
                onMoveToTrash={onMoveToTrash}
                onMark={onMark}
                onRestore={onRestore}
                isTrashMode={isTrashMode}
                collections={collections}
                onAddToCollections={onAddToCollections}
                onRemoveFromCollection={onRemoveFromCollection}
                onShowToast={onShowToast}
                onImageClick={onImageClick}
              />
            ))}
            {hasMore && loading && (
              <div className="col-span-full flex justify-center p-4">
                <div className="flex items-center text-gray-400 text-sm">
                  <Icon name={ICONS.SEARCH} size={16} className="mr-2 animate-spin" />
                  {t.galleryPage?.loadingMore || 'Loading more...'}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6">
            {/* Two-column list layout for wider screens */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {images.map((image, index) => (
                <GalleryImageCard
                  key={image.id}
                  image={image}
                  index={index}
                  viewMode={viewMode}
                  language={language}
                  organizationId={organizationId}
                  selectionMode={selectionMode}
                  isSelected={selectedIds.has(image.id)}
                  onToggleSelection={onToggleSelection}
                  onDragSelectStart={onDragSelectStart}
                  onDragSelectEnter={onDragSelectEnter}
                  isDragSelecting={isDragSelecting}
                  onMoveToTrash={onMoveToTrash}
                  onMark={onMark}
                  onRestore={onRestore}
                  isTrashMode={isTrashMode}
                  collections={collections}
                  onAddToCollections={onAddToCollections}
                  onRemoveFromCollection={onRemoveFromCollection}
                  onShowToast={onShowToast}
                  onImageClick={onImageClick}
                />
              ))}
            </div>
            {hasMore && loading && (
              <div className="flex justify-center p-4">
                <div className="flex items-center text-gray-400 text-sm">
                  <Icon name={ICONS.SEARCH} size={16} className="mr-2 animate-spin" />
                  {t.galleryPage?.loadingMore || 'Loading more...'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sentinel div for IntersectionObserver infinite scroll */}
      <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />
    </div>
  );
};

export default VirtualizedImageGrid;